// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useState } from 'react';
import { AuthService } from '../../services/authService';

// Environment detection
const isDev =
  process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === 'dev' ||
  process.env.NODE_ENV === 'development';

const LOGOUT_PATH =
  process.env.REACT_APP_LOGOUT_PATH ||
  (isDev ? '/auth/logout' : '/api/auth/logout');

// Helper function to get a cookie value by name
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

interface LogoutButtonProps {
  className?: string;
  children?: React.ReactNode;
}

export const LogoutButton: React.FC<LogoutButtonProps> = ({ 
  className = '', 
  children = 'Sign Out' 
}) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent double taps

    setIsLoggingOut(true);

    if (isDev) {
      // Development: use client-side logout
      await AuthService.initiateLogout();
    } else {
      // Production: POST to server with CSRF token to clear HttpOnly cookies
      try {
        // Clear any local storage or session storage
        localStorage.removeItem('auth_state');
        sessionStorage.clear();

        // Get CSRF token from cookie
        const csrfToken = getCookie('ehta_csrf');

        // Debug: log CSRF token presence (not value for security)
        console.log('Logout: CSRF token present:', !!csrfToken, 'length:', csrfToken?.length);
        console.log('Logout: All cookies:', document.cookie);

        // Make POST request with CSRF header
        // Note: CloudFront whitelists X-CSRF-Token, not X-CSRF
        const response = await fetch(LOGOUT_PATH, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken && { 'X-CSRF-Token': csrfToken })
          }
        });

        console.log('Logout response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          // Redirect to Cognito logout URL returned by server
          if (data.logout_url) {
            window.location.assign(data.logout_url);
          } else {
            // Fallback to home if no logout URL
            window.location.assign('/');
          }
        } else {
          // On error, still redirect to home
          console.error('Logout request failed:', response.status);
          window.location.assign('/');
        }
      } catch (e) {
        console.error('Logout error:', e);
        // Fallback redirect to home on error
        window.location.assign('/');
      }
    }
  };

  return (
    <button
      onClick={handleLogout}
      className={`logout-button ${className}`}
      type="button"
      aria-label="Sign out of EasyHTA"
      disabled={isLoggingOut}
    >
      {isLoggingOut ? 'Signing out...' : children}
    </button>
  );
};
